import { ClinicalVariableReadDto } from "functions/api/library/interface";
import { ComparatorType } from "./interface";
import { CompanionVariable } from "./VariableDto";
import { isWhereClause, WhereClauseOp } from "./WhereClauseOp";

/**
 * Represents data that can be validated as a where clause.
 * This includes both complete WhereClauseOp objects and partial form state during editing.
 *
 * @interface ValidatableWhereClause
 * @property dataset - The name of the clinical dataset.
 * @property nameVariable - The name of the clinical variable.
 * @property comparator - The name of the comparison operator (e.g., "Equals", "In").
 * @property value - The value(s) to compare against (optional, depending on comparator type).
 * @property companionVariables - Optional array of companion variables with their types and values.
 */
export interface ValidatableWhereClause {
  dataset?: string;
  nameVariable?: string;
  comparator?: string;
  value?: string;
  companionVariables?: CompanionVariable[];
}

/**
 * Union type representing all supported input formats for validateWhereClause.
 */
export type ValidatableWhereClauseInput =
  | WhereClauseOp
  | ValidatableWhereClause
  | {
      selectedDataset: string;
      selectedVariable?: ClinicalVariableReadDto;
      comparator?: string;
      value?: string;
      companionVariables: CompanionVariable[];
    };

/**
 * Validates whether a where clause has all required data.
 * Works with both WhereClauseOp objects and form state.
 * Checks that dataset and variable are present.
 * A comparator is optional - if not provided, the clause is still valid (references the variable only).
 * Validates that a value is provided when a comparator is present and requires one (rhsType is not 'None').
 * Ensures all companion variables have a non-empty value selected.
 *
 * @param data - Either a WhereClauseOp, ValidatableWhereClause, or form state to validate
 * @param comparatorTypes - Array of available comparator types to check rhsType requirements
 * @returns True if the where clause has all required fields populated correctly
 */
export const validateWhereClause = (
  data: ValidatableWhereClauseInput,
  comparatorTypes?: ComparatorType[],
): boolean => {
  // Convert to ValidatableWhereClause format based on input type
  const validatable: ValidatableWhereClause = isWhereClause(data)
    ? {
        dataset: data.left?.dataset,
        nameVariable: data.left?.nameVariable,
        comparator: data.comparator,
        value: data.right,
        companionVariables: data.left?.companionVariables,
      }
    : "selectedDataset" in data
      ? {
          dataset: data.selectedDataset,
          nameVariable: data.selectedVariable?.name,
          comparator: data.comparator,
          value: data.value,
          companionVariables: data.companionVariables,
        }
      : data;

  const { dataset, nameVariable, comparator, value, companionVariables } = validatable;

  // Check basic required fields (dataset and variable are always required)
  if (!dataset || !nameVariable) {
    return false;
  }

  // Check that all companion variables have a value selected
  if (
    companionVariables?.some(
      (companion) => !companion.variable || companion.variable.split(".")[0] !== dataset,
    )
  ) {
    return false;
  }

  // If no comparator is selected, the clause is still valid (just references the variable)
  if (!comparator) {
    return true;
  }

  // Check if comparator requires a value (only validate if comparatorType is found)
  const comparatorType = comparatorTypes?.find((ct) => ct.name === comparator);
  if (comparatorType && comparatorType.rhsType !== "None" && !value) {
    return false;
  }

  return true;
};
